import type { KeyedMutator } from 'swr';
import { useTranslation } from 'react-i18next';
import { I18nNamespace } from '@/lib/i18n-namespaces';
import { EngineService } from '@/services/engine';
import type { ReportWithEvents } from '@/types/report';
import type { ReportOrderPlacement } from '../utils/moveOrderedItem';
import { moveReportPrompt } from '../utils/moveReportPrompt';
import type {
    ReportOrderMessages,
    ReportOrderMutation,
} from './useReportOrderMutation';

export interface ReportPromptReordering {
    isPending: boolean;
    move: (
        sourcePromptId: string,
        targetPromptId: string,
        placement: ReportOrderPlacement,
    ) => void;
}

interface UseReportPromptOrderingParams {
    reportId: string;
    mutate: KeyedMutator<ReportWithEvents>;
    orderMutation: ReportOrderMutation;
}

export function useReportPromptOrdering({
    reportId,
    mutate,
    orderMutation,
}: UseReportPromptOrderingParams): ReportPromptReordering {
    const { t } = useTranslation(I18nNamespace.REPORTS);

    const messages: ReportOrderMessages = {
        success: t('detail.promptOrder.success'),
        stale: t('detail.promptOrder.stale'),
        error: t('detail.promptOrder.error'),
    };

    const applyMove = (
        currentReport: ReportWithEvents,
        sourcePromptId: string,
        targetPromptId: string,
        placement: ReportOrderPlacement,
    ): ReportWithEvents => ({
        ...currentReport,
        events: moveReportPrompt(
            currentReport.events,
            sourcePromptId,
            targetPromptId,
            placement,
        ),
    });

    const move = (
        sourcePromptId: string,
        targetPromptId: string,
        placement: ReportOrderPlacement,
    ) =>
        orderMutation.run(messages, async (currentReport) => {
            await mutate(
                async (cachedReport) => {
                    if (!cachedReport) {
                        return cachedReport;
                    }
                    const { orderVersion } =
                        await EngineService.reorderReportPrompt(reportId, {
                            sourcePromptId,
                            targetPromptId,
                            placement,
                            expectedOrderVersion: currentReport.orderVersion,
                        });
                    return {
                        ...applyMove(
                            cachedReport,
                            sourcePromptId,
                            targetPromptId,
                            placement,
                        ),
                        orderVersion,
                    };
                },
                {
                    optimisticData: applyMove(
                        currentReport,
                        sourcePromptId,
                        targetPromptId,
                        placement,
                    ),
                    rollbackOnError: true,
                    revalidate: false,
                },
            );
        });

    return {
        isPending: orderMutation.isPending,
        move: (sourcePromptId, targetPromptId, placement) =>
            void move(sourcePromptId, targetPromptId, placement),
    };
}
